import { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { UserCircle, MapPin, Users, GraduationCap, Heart, Stethoscope, ChevronDown } from 'lucide-react';
import { filterBeltsForBirthDate, beltAgeRangeText } from '../lib/beltRules';
// FASE E — documento em UM CAMPO SÓ (CPF ou RG), inteligente e opcional
import CampoDocumentoInteligente from './CampoDocumentoInteligente';
// FASE 1 — nacionalidade (com sugestões de países) e naturalidade
import CampoPais from './CampoPais';
import { normalizarNaturalidade } from '../lib/paises';
// FASE 2 — data da graduação ("faixa atual desde")
import CampoDataGraduacao from './CampoDataGraduacao';

/**
 * Empty profile structure — mirrors the athlete schema.
 * Use this to initialize a profile state in any page that needs to capture
 * the same personal data we collect for athletes (used by UsersAdminPage for
 * professors so the same person can also be an athlete competitor).
 */
export const emptyPersonProfile = () => ({
  birth_date: '', sex: 'M',
  weight: '', belt: '', team_id: '', professor_id: '', federation_ids: [],
  phone: '', start_date: '',
  // FASE E — documento em UM CAMPO SÓ (OPCIONAL) + responsável legal (opcional)
  documento: '', documento_tipo: 'auto',
  guardian_name: '', guardian_relationship: '', guardian_cpf: '',
  // FASE 1 — nacionalidade e naturalidade (opcionais; não nascem preenchidos)
  nationality: '', birth_place: '',
  // FASE 2 — data da graduação (opcional)
  belt_since: '',
  father_name: '', mother_name: '', home_phone: '', responsible_cell_phone: '',
  address: '', neighborhood: '', city: '', state: '', zip_code: '',
  education_level: '', school_name: '', school_grade: '', school_shift: '',
  has_special_needs: false, special_needs_description: '',
  has_health_issues: false, health_issues_description: '',
  blood_type: '', height_cm: '',
  medical_certificate_date: '', doctor_name: '', doctor_crm: '',
});

const Section = ({ icon, title, testid, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border border-[#27272A] rounded-lg bg-[#0F0F0F]">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#1A1A1A] rounded-lg"
        >
          <span className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
            {icon} {title}
          </span>
          <ChevronDown size={16} className={`text-[#71717A] transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 pt-1">{children}</CollapsibleContent>
    </Collapsible>
  );
};

/**
 * <PersonProfileFields> — shared profile fields used wherever a "person" is
 * captured (athlete or professor). Caller is responsible for the Nome field
 * since it usually belongs to the user-level form.
 */
export default function PersonProfileFields({ formData, setFormData, teams = [], professors = [], belts = [], beltOriginal = '' }) {
  const set = (patch) => setFormData((prev) => ({ ...prev, ...patch }));
  const inputClass = "bg-[#1A1A1A] border-[#27272A] text-white";

  return (
    <div className="space-y-5">
      {/* ===== IDENTIFICAÇÃO ===== */}
      <Section icon={<UserCircle size={16} />} title="Identificação" testid="profile-section-id" defaultOpen>
        {/* Data de Nascimento e o documento ficam LADO A LADO. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-birth-date">Data de Nascimento</Label>
            <Input type="date" value={formData.birth_date || ''} onChange={(e) => set({ birth_date: e.target.value })} className={inputClass} id="profile-birth-date" data-testid="profile-birth-date" />
          </div>
          {/* FASE E — DOCUMENTO EM UM CAMPO SÓ: reconhece CPF (com dígito
              verificador) ou RG (máscara leve). Opcional. */}
          <div data-testid="profile-documento-block">
            <CampoDocumentoInteligente
              label="CPF ou RG"
              value={formData.documento}
              tipo={formData.documento_tipo}
              onChange={(v) => set({ documento: v })}
              onTipoChange={(t) => set({ documento_tipo: t })}
              testId="profile-documento"
              hint="Opcional. Digite o CPF ou o RG — o campo se vira sozinho."
            />
          </div>
        </div>
        {/* FASE 1 — Nacionalidade e Naturalidade na mesma linha. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start mt-3">
          <CampoPais
            value={formData.nationality}
            onChange={(v) => set({ nationality: v })}
            testId="profile-nationality"
            hint="Opcional. Comece a digitar para ver as sugestões."
          />
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-birth-place">Naturalidade</Label>
            <Input
              value={formData.birth_place || ''}
              onChange={(e) => set({ birth_place: e.target.value })}
              onBlur={(e) => set({ birth_place: normalizarNaturalidade(e.target.value) })}
              placeholder="Ex: Rio de Janeiro/RJ"
              id="profile-birth-place" data-testid="profile-birth-place"
              className={inputClass}
            />
            <p className="text-micro text-[#71717A] leading-tight">
              Opcional. Cidade e estado onde nasceu (como na ficha).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-sex">Sexo</Label>
            <Select value={formData.sex || 'M'} onValueChange={(v) => set({ sex: v })}>
              <SelectTrigger className={inputClass} id="profile-sex" data-testid="profile-sex"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="M" className="text-white">Masculino</SelectItem>
                <SelectItem value="F" className="text-white">Feminino</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-weight">Peso (kg)</Label>
            <Input type="number" step="0.1" value={formData.weight || ''} onChange={(e) => set({ weight: e.target.value })} placeholder="70.5" className={inputClass} id="profile-weight" data-testid="profile-weight" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-belt">Faixa</Label>
            <Select value={formData.belt || "none"} onValueChange={(v) => set({ belt: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass} id="profile-belt" data-testid="profile-belt"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                {filterBeltsForBirthDate(belts, formData.birth_date).map((b) => (
                  <SelectItem key={b._id} value={b.name} className="text-white hover:bg-[#27272A]">{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.belt && beltAgeRangeText(formData.belt) && (
              <p className="text-micro text-[#71717A]">{beltAgeRangeText(formData.belt)} (CBJJ/IBJJF)</p>
            )}
          </div>
        </div>
        {/* FASE 2 — desde quando o atleta tem a faixa atual (opcional). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start mt-3">
          <CampoDataGraduacao
            value={formData.belt_since}
            onChange={(v) => set({ belt_since: v })}
            belt={formData.belt}
            beltOriginal={beltOriginal}
            startDate={formData.start_date}
            birthDate={formData.birth_date}
            testId="profile-belt-since"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-phone">Telefone</Label>
            <Input value={formData.phone || ''} onChange={(e) => set({ phone: e.target.value })} placeholder="(00) 00000-0000" inputMode="tel" maxLength={20} className={inputClass} id="profile-phone" data-testid="profile-phone" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-start-date">Data de Início no BJJ</Label>
            <Input type="date" value={formData.start_date || ''} onChange={(e) => set({ start_date: e.target.value })} className={inputClass} id="profile-start-date" data-testid="profile-start-date" />
          </div>
        </div>
      </Section>

      {/* ===== VÍNCULOS ===== */}
      <Section icon={<Users size={16} />} title="Vínculos (Equipe e Supervisor)" testid="profile-section-vinculos" defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-team">Equipe</Label>
            <Select value={formData.team_id || "none"} onValueChange={(v) => set({ team_id: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass} id="profile-team" data-testid="profile-team"><SelectValue placeholder="Selecione uma equipe" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Nenhuma</SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t._id} value={t._id} className="text-white hover:bg-[#27272A]">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-professor">Professor supervisor</Label>
            <Select value={formData.professor_id || "none"} onValueChange={(v) => set({ professor_id: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass} id="profile-professor" data-testid="profile-professor"><SelectValue placeholder="Selecione um professor" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Nenhum</SelectItem>
                {professors.map((p) => (
                  <SelectItem key={p._id} value={p._id} className="text-white hover:bg-[#27272A]">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* ===== RESPONSÁVEIS ===== */}
      <Section icon={<Users size={16} />} title="Responsáveis e Contato Estendido" testid="profile-section-responsaveis">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-pai">Pai</Label><Input id="profile-pai" value={formData.father_name || ''} onChange={(e) => set({ father_name: e.target.value })} className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-mae">Mãe</Label><Input id="profile-mae" value={formData.mother_name || ''} onChange={(e) => set({ mother_name: e.target.value })} className={inputClass} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-tel-residencial">Tel. Residencial</Label><Input id="profile-tel-residencial" value={formData.home_phone || ''} onChange={(e) => set({ home_phone: e.target.value })} inputMode="tel" maxLength={20} className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-celular-do-responsavel">Celular do Responsável</Label><Input id="profile-celular-do-responsavel" value={formData.responsible_cell_phone || ''} onChange={(e) => set({ responsible_cell_phone: e.target.value })} inputMode="tel" maxLength={20} className={inputClass} /></div>
        </div>
      </Section>

      {/* ===== ENDEREÇO ===== */}
      <Section icon={<MapPin size={16} />} title="Endereço" testid="profile-section-endereco">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2 col-span-2"><Label className="text-[#A1A1AA]" htmlFor="profile-endereco">Endereço (Rua/Av e número)</Label><Input id="profile-endereco" value={formData.address || ''} onChange={(e) => set({ address: e.target.value })} placeholder="Rua Tal, 123" className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-cep">CEP</Label><Input id="profile-cep" value={formData.zip_code || ''} onChange={(e) => set({ zip_code: e.target.value })} placeholder="00000-000" inputMode="numeric" maxLength={9} className={inputClass} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-bairro">Bairro</Label><Input id="profile-bairro" value={formData.neighborhood || ''} onChange={(e) => set({ neighborhood: e.target.value })} className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-cidade">Cidade</Label><Input id="profile-cidade" value={formData.city || ''} onChange={(e) => set({ city: e.target.value })} className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-estado">Estado</Label><Input id="profile-estado" value={formData.state || ''} onChange={(e) => set({ state: e.target.value })} placeholder="RJ" maxLength={2} className={inputClass} /></div>
        </div>
      </Section>

      {/* ===== ESCOLA ===== */}
      <Section icon={<GraduationCap size={16} />} title="Dados Escolares" testid="profile-section-escolar">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-escolaridade">Escolaridade</Label>
            <Select value={formData.education_level || "none"} onValueChange={(v) => set({ education_level: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass}id="profile-escolaridade" ><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">—</SelectItem>
                {['Educação Infantil','Fundamental','Médio','Técnico','Superior','Pós-graduação'].map(v => <SelectItem key={v} value={v} className="text-white">{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-escola">Escola</Label><Input id="profile-escola" value={formData.school_name || ''} onChange={(e) => set({ school_name: e.target.value })} className={inputClass} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-serie">Série</Label><Input id="profile-serie" value={formData.school_grade || ''} onChange={(e) => set({ school_grade: e.target.value })} placeholder="Ex: 5º ano" className={inputClass} /></div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-turno">Turno</Label>
            <Select value={formData.school_shift || "none"} onValueChange={(v) => set({ school_shift: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass}id="profile-turno" ><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">—</SelectItem>
                <SelectItem value="manha" className="text-white">Manhã</SelectItem>
                <SelectItem value="tarde" className="text-white">Tarde</SelectItem>
                <SelectItem value="noite" className="text-white">Noite</SelectItem>
                <SelectItem value="integral" className="text-white">Integral</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* ===== SAÚDE ===== */}
      <Section icon={<Heart size={16} />} title="Saúde" testid="profile-section-saude">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="profile-possui-necessidades-especiais" checked={!!formData.has_special_needs} onChange={(e) => set({ has_special_needs: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            <Label className="text-white" htmlFor="profile-possui-necessidades-especiais">Possui necessidades especiais?</Label>
          </div>
          {formData.has_special_needs && (
            <Input value={formData.special_needs_description || ''} onChange={(e) => set({ special_needs_description: e.target.value })} placeholder="Descreva..." className={inputClass} />
          )}
          <div className="flex items-center gap-3 mt-2">
            <input type="checkbox" id="profile-possui-algum-problema-de-saude" checked={!!formData.has_health_issues} onChange={(e) => set({ has_health_issues: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            <Label className="text-white" htmlFor="profile-possui-algum-problema-de-saude">Possui algum problema de saúde?</Label>
          </div>
          {formData.has_health_issues && (
            <Input value={formData.health_issues_description || ''} onChange={(e) => set({ health_issues_description: e.target.value })} placeholder="Descreva..." className={inputClass} />
          )}
        </div>
      </Section>

      {/* ===== MÉDICO ===== */}
      <Section icon={<Stethoscope size={16} />} title="Dados Médicos" testid="profile-section-medico">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]" htmlFor="profile-tipo-sanguineo">Tipo Sanguíneo</Label>
            <Select value={formData.blood_type || "none"} onValueChange={(v) => set({ blood_type: v === "none" ? "" : v })}>
              <SelectTrigger className={inputClass}id="profile-tipo-sanguineo" ><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">—</SelectItem>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => <SelectItem key={bt} value={bt} className="text-white">{bt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-altura">Altura (cm)</Label><Input type="number" step="0.5" id="profile-altura" value={formData.height_cm || ''} onChange={(e) => set({ height_cm: e.target.value })} placeholder="170" className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-data-atestado-medico">Data do Atestado Médico</Label><Input type="date" id="profile-data-atestado-medico" value={formData.medical_certificate_date || ''} onChange={(e) => set({ medical_certificate_date: e.target.value })} className={inputClass} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-nome-do-medico">Nome do Médico</Label><Input id="profile-nome-do-medico" value={formData.doctor_name || ''} onChange={(e) => set({ doctor_name: e.target.value })} className={inputClass} /></div>
          <div className="space-y-2"><Label className="text-[#A1A1AA]" htmlFor="profile-crm">CRM</Label><Input id="profile-crm" value={formData.doctor_crm || ''} onChange={(e) => set({ doctor_crm: e.target.value })} className={inputClass} /></div>
        </div>
      </Section>
    </div>
  );
}
